import React, { useState } from 'react';
import { Trans, t, plural } from '@lingui/macro';
import classnames from 'classnames';
import ReactHtmlParser from 'html-react-parser';
import { Helmet } from 'react-helmet-async';

import ActivityFeed from 'Components/ActivityFeed/ActivityFeed';
import AdminMessageController from 'Components/AdminMessage/AdminMessageController';
import AndroidBanner from 'Components/AndroidBanner/AndroidBanner';
import BigCities from 'Components/BigCities/BigCities';
import CategoryGroups from 'Components/CategoryGroups/CategoryGroups';
import CityList from 'Components/CityList/CityList';
import { CurrentUserConsumer, TCurrentUserState } from 'Components/CurrentUser/CurrentUser';
import FeaturedCategories from 'Components/FeaturedCategories/FeaturedCategories';
import Feedbacks from 'Components/Feedbacks/Feedbacks';
import LatestRequests from 'Components/LatestRequests/LatestRequests';
import ProvidersSlider from 'Components/ProvidersSlider/ProvidersSlider';
import PwfSupportedBadge from 'Components/PwfSupportedBadge/PwfSupportedBadge';
import ReviewsSchema from 'Components/ReviewsSchema/ReviewsSchema';
import SeoLinking from 'Components/SeoLinking/SeoLinking';
import RedirectWithStatus from 'common/CustomRoutes/RedirectWithStatus';
import PageView from 'common/PageView/PageView';
import { isWindow } from 'common/helpers/helpers';
import shouldHideRatings from 'common/helpers/shouldHideRatings';
import config from 'config/config';
import seoLinking from 'config/seoLinking';
import categoryIcon from 'uikit/Icons/Categories';
import { TextP1 } from 'uikit/Text';
import { nearbyCitiesQuery } from 'utils/graphql/geolocation';
import routes from 'utils/routeTranslator';

import BreadcrumbSchema from './BreascrumbSchema';
import {
  L2CategoryQuery,
  L2CategoryCityQuery,
  remoteJobCategory,
  l2Redirect,
  getSeoTitleByLength,
  extractFeaturedL4,
  TL2Category,
  TCity,
  L2CategoryResponse,
  L2CategoryCityResponse,
} from './L2Category.helpers';
import './L2Category.scss';
import L2CategoryNav from './L2CategoryNav/L2CategoryNav';
import PwfPromoBanner from './PwfPromoBanner/PwfPromoBanner';
import ContactlessCategories from './RemoteJobFeatures/ContactlessCategories';
import { useQuery } from '@apollo/client';
import LoadingPlaceholder from 'Components/Settings/Components/LoadingPlaceholder';
import { RouteProps } from 'react-router-dom';

type L2CategoryProps = RouteProps & {
  match: {
    params: {
      slug: string;
      city?: string;
    };
  };
};

type L2CategoryVars = {
  slug: string;
  city?: string;
  showHiddenCategories: boolean;
};

const monthLocativeCase = (monthNumber: number) => {
  return plural(monthNumber, {
    1: 'w styczniu',
    2: 'w lutym',
    3: 'w marcu',
    4: 'w kwietniu',
    5: 'w maju',
    6: 'w czerwcu',
    7: 'w lipcu',
    8: 'w sierpniu',
    9: 'we wrześniu',
    10: 'w październiku',
    11: 'w listopadzie',
    12: 'w grudniu',
  });
};

const L2Category = (props: L2CategoryProps) => {
  const [locationPathname, setLocationPathname] = useState('');
  const { match } = props;
  const {
    params: { city, slug },
  } = match;

  // checking if it matches any slug alias for redirection purposes
  const slugRedirect = l2Redirect[slug as keyof typeof l2Redirect];
  const isRedirection = Boolean(slugRedirect);

  const {
    data: l2CategoriesData,
    loading: categoriesLoading,
    error: l2CategoriesError,
  } = useQuery<L2CategoryResponse, L2CategoryVars>(L2CategoryQuery, {
    variables: {
      slug: slug,
      city: city,
      showHiddenCategories: false,
    },
    skip: isRedirection,
  });

  const {
    data: cityBySlugData,
    loading: cityBySlugLoading,
    error: cityBySlugError,
  } = useQuery<L2CategoryCityResponse>(L2CategoryCityQuery, {
    variables: { slug: city },
    skip: isRedirection || !city,
  });

  const { data: cityData } = useQuery(nearbyCitiesQuery, {
    variables: {
      slug: city,
    },
    skip: isRedirection || !city,
  });

  if (isRedirection) {
    if (slugRedirect === '/') {
      return <RedirectWithStatus status={301} to={routes.get('front::index')} />;
    }

    return (
      <RedirectWithStatus
        status={301}
        to={routes.get('front::categories.type', {
          slug: slugRedirect,
          city: city || undefined,
        })}
      />
    );
  }

  if (categoriesLoading || cityBySlugLoading) {
    return <LoadingPlaceholder />;
  }

  if (l2CategoriesError || cityBySlugError) {
    return <RedirectWithStatus status={301} to={routes.get('front::categories.type', { slug: match.params.slug })} />;
  }

  const {
    id,
    name,
    l4Groups,
    requests,
    feedbacks,
    feedbacksInfo,
    providerCount,
    providers,
    seoDescription,
    ogDescription,
    questionsAndAnswers,
    isPayWithFixly,
  } = l2CategoriesData?.l2BySlug as TL2Category;

  const cityBySlug = cityBySlugData?.cityBySlug as TCity;
  const cityDetails = cityBySlug
    ? {
        id: cityBySlug?.id,
        name: cityBySlug?.name,
        slug: cityBySlug?.slug,
        region: cityBySlug?.region.name,
        lat: cityBySlug?.lat,
        lng: cityBySlug?.long,
        nameLocativeCase: cityBySlug?.nameLocativeCase,
      }
    : {};

  const featuredCategories = extractFeaturedL4(l4Groups);
  const l4CategoriesGroups = l4Groups.filter((categoryGroup) => categoryGroup.name !== 'featured');

  const showDescription: boolean = Boolean(seoDescription) && !city;
  const shouldHideRatingInfo = shouldHideRatings(feedbacksInfo.count, feedbacksInfo.averageValue);

  const providersCount: number = providerCount < 10 ? 10 : providerCount;
  const feedbacksCount: number = feedbacksInfo.count < 10 ? 10 : feedbacksInfo.count;

  const now = new Date();
  const monthNumber = now.getMonth() + 1;
  const year = now.getFullYear();

  const pageTitle: string = cityDetails?.slug
    ? getSeoTitleByLength(name, cityDetails?.name, providersCount)
    : t`${name} w Twojej okolicy | ${providersCount} wykonawców | Najlepsi ${monthLocativeCase(monthNumber)} ${year}`;

  const descriptionWithCity = t`${name}: ${providersCount} wykonawców ${
    cityDetails?.nameLocativeCase || cityDetails?.name
  } - sprawnie, wygodnie, lokalnie! Dodaj zapytanie i szybko nawiąż kontakt. ${feedbacksCount} opinii klientów!`;

  const pageSeoDescription: string = cityDetails?.slug ? descriptionWithCity : ogDescription;

  const showPwfBanner = isPayWithFixly;

  const title = city ? `${name} ${cityDetails?.nameLocativeCase || cityDetails?.name}` : name;

  const titleCN = classnames('l2Category__header', {
    l2Category__header_withDescription: remoteJobCategory(id),
  });

  return (
    <CurrentUserConsumer>
      {(context: TCurrentUserState | undefined) => {
        if (isWindow && context && context.currentUser && context.currentUser.userId) {
          setLocationPathname(window.location.pathname);
          // Redirect SP
          if (context.currentUser.isProvider) {
            window.location.href = routes.get('front::dashboard');
            return null;
          }
        }

        return (
          <div className="l2Category">
            <Helmet>
              <title>{pageTitle}</title>
              <meta
                property="og:title"
                content={`${name} | ${cityDetails?.name ? `${cityDetails?.name} | ` : ''}${t`Kategorie usług`} | ${
                  config.SITE_NAME
                }`}
              />
              <meta property="og:description" content={pageSeoDescription} />
              <meta name="msapplication-tooltip" content={pageSeoDescription} />
              <meta name="description" content={pageSeoDescription} />
              <link
                rel="canonical"
                href={
                  config.SITE_URL +
                  routes.get('front::categories.type', {
                    slug,
                    city: cityDetails?.slug || undefined,
                  })
                }
              />
            </Helmet>
            <PageView
              key={`${slug}.${cityDetails?.slug || ''}`}
              pageLabel="browse_category"
              city_name={cityDetails?.name || ''}
              city={parseInt(cityDetails?.id || '', 10)}
              l1_id={parseInt(id, 10)}
              l2_id={parseInt(id, 10)}
              l1_slug={slug}
              l2_slug={slug}
            />
            <AndroidBanner />
            <div className="l2Category__back">
              <a
                href={cityDetails?.slug ? routes.get('front::categories.type', { slug }) : '/'}
                className="l2Category__backBtn"
              >
                <Trans>Wróć</Trans>
              </a>
            </div>

            {showPwfBanner && <PwfPromoBanner />}

            <div className={titleCN}>
              <div className="l2Category__icon">{categoryIcon(slug)}</div>
              <h1 className="l2Category__title">{title}</h1>
              {showPwfBanner && <PwfSupportedBadge className="l2Category__pwfBadge" />}
              <div className="l2Category__topCategoriesNav">
                <L2CategoryNav />
              </div>
            </div>

            <div className="l2Category__categoriesFeed">
              <div className="l2Category__featuredCategories">
                {remoteJobCategory(id) && seoDescription && (
                  <div className="l2Category__titleDescription">
                    <TextP1 as="div">{ReactHtmlParser(seoDescription)}</TextP1>
                  </div>
                )}

                <FeaturedCategories categories={featuredCategories} city={cityDetails?.slug} />
              </div>
              <div className="l2Category__feed">
                {/* @ts-expect-error type mismatch */}
                <ActivityFeed l2Slug={slug} hideAvatarsDesktop desktopShortVersion />
              </div>
              <div className="l2Category__categories">
                <CategoryGroups categories={l4CategoriesGroups} city={cityDetails?.slug} l2Id={id} />
              </div>
              {remoteJobCategory(id) && (
                <div className="l2Category__categories">
                  <ContactlessCategories
                    l4Groups={l4CategoriesGroups}
                    city={cityDetails?.slug}
                    l2Id={id}
                    sectionHeader={
                      <div>
                        <div className={classnames(titleCN, 'l2Category__header_inside')}>
                          <div className="l2Category__icon">{categoryIcon(slug)}</div>
                          <h1 className="l2Category__title">
                            <Trans>Zleć usługę bez kontaktu</Trans>{' '}
                            {city && (cityDetails?.nameLocativeCase || cityDetails?.name)}
                          </h1>
                        </div>
                        <div className="l2Category__titleDescription">
                          <TextP1 as="div">
                            <Trans id="remote_l2_category_description">
                              Czy wiesz, że jest wiele usług na Fixly, których wykonanie nie wymaga Twojego spotkania z
                              wykonawcą? Meble do transportu możesz przygotować na odbiór w korytarzu, a uszkodzony
                              płaszcz wysłać do pracowni krawieckiej kurierem. Daj znać, czego potrzebujesz i ustal
                              szczegóły bezpiecznej realizacji usługi z wykonawcą.
                            </Trans>
                          </TextP1>
                        </div>
                      </div>
                    }
                  />
                </div>
              )}
            </div>

            {!cityDetails?.slug && Array.isArray(feedbacks) && feedbacks.length > 0 && (
              <div className="l2Category__feedbacks">
                <h2 className="l2Category__subtitle l2Category__subtitle_withDescription">
                  <Trans>Jak wykonawców oceniają inni użytkownicy Fixly?</Trans>
                </h2>
                <p className="l2Category__description">
                  {shouldHideRatingInfo ? (
                    <Trans>
                      Na Fixly mamy {providerCount} wykonawców w kategorii {name}.
                    </Trans>
                  ) : (
                    <Trans>
                      Na Fixly mamy {providerCount} wykonawców w kategorii {name}, ocenianych średnio na{' '}
                      <span className="l2Category__rating">{feedbacksInfo.averageValue.toFixed(1)}</span> (na podstawie{' '}
                      {feedbacksInfo.count} opinii)
                    </Trans>
                  )}
                </p>
                <Feedbacks feedbacks={feedbacks} type="slider" showRatedLabel />
              </div>
            )}

            {cityDetails?.slug && Array.isArray(providers) && providers.length > 0 && (
              <div className="l2Category__providers">
                <h2 className="l2Category__subtitle l2Category__subtitle_withDescription">
                  <Trans>
                    Wykonawcy usługi {name} {cityDetails?.nameLocativeCase || cityDetails?.name}
                  </Trans>
                </h2>
                <p className="l2Category__description">
                  {shouldHideRatingInfo ? (
                    <Trans>
                      W{' '}
                      {cityDetails?.nameLocativeCase
                        ? cityDetails?.nameLocativeCase.replace('w ', '')
                        : cityDetails?.name || ''}
                      mamy {providerCount} wykonawców w kategorii {name}.
                    </Trans>
                  ) : (
                    <Trans>
                      W{' '}
                      {cityDetails?.nameLocativeCase
                        ? cityDetails?.nameLocativeCase.replace('w ', '')
                        : cityDetails?.name || ''}
                      mamy{' '}
                      <span className="item">
                        <span className="fn">{providerCount}</span>
                      </span>{' '}
                      wykonawców w kategorii {name}, ocenianych średnio na{' '}
                      <span className="rating">
                        <span className="l2Category__rating">{feedbacksInfo.averageValue.toFixed(1)}</span> (na
                        podstawie <span className="count">{feedbacksInfo.count}</span> opinii)
                      </span>
                    </Trans>
                  )}
                </p>
                <ProvidersSlider
                  providers={providers}
                  cityName={cityDetails?.name}
                  cityLat={Number(cityDetails?.lat || 0)}
                  cityLong={Number(cityDetails?.lng || 0)}
                  regionName={cityDetails?.region}
                />
              </div>
            )}

            {city && Array.isArray(requests) && requests.length > 0 && (
              <div className="l2Category__requests">
                <h2 className="l2Category__subtitle">
                  <Trans>
                    {name}: ostatnie zapytania {cityDetails?.nameLocativeCase || cityDetails?.name}
                  </Trans>
                </h2>
                <LatestRequests requests={requests} />
              </div>
            )}

            {!remoteJobCategory(id) && (
              <div className="l2Category__seoCities">
                {showDescription && seoDescription && (
                  <div className="l2Category__seo">
                    <h2 className="l2Category__subtitle">{name}</h2>
                    <div className="l2Category__seoText">{ReactHtmlParser(seoDescription)}</div>
                  </div>
                )}
                <div className="l2Category__cities">
                  <h2 className="l2Category__subtitle">
                    {city ? <Trans>{name} w miastach z pobliżu</Trans> : <Trans>{name} w różnych miastach</Trans>}
                  </h2>
                  {city ? (
                    <CityList categorySlug={slug} categoryName={name} multiColumn isL2 cities={cityData.nearbyCities} />
                  ) : (
                    <BigCities categorySlug={slug} categoryName={name} multiColumn isL2 />
                  )}
                </div>

                {questionsAndAnswers && (
                  <div className="l2Category__seo">
                    <div className="l2Category__seoText">{ReactHtmlParser(questionsAndAnswers)}</div>
                  </div>
                )}
              </div>
            )}

            {seoLinking && seoLinking[locationPathname] && (
              <div className="l2Category__seoLinking">
                <h2 className="l2Category__subtitle">
                  <Trans>Najpopularniejsze wyszukiwania</Trans>
                </h2>
                <SeoLinking links={seoLinking[locationPathname]} />
              </div>
            )}

            {context?.currentUser && <AdminMessageController />}
            <BreadcrumbSchema l2={l2CategoriesData?.l2BySlug || { slug: slug, name: name }} city={cityBySlug} />
            {cityDetails?.slug && (
              <ReviewsSchema
                isL2
                slug={slug}
                city={cityDetails?.slug || ''}
                imgUrl=""
                title={title}
                description={pageSeoDescription}
                cityName={cityDetails?.name}
                regionName={cityDetails?.region}
                lat={cityDetails?.lat}
                long={cityDetails?.lng}
                feedbacks={feedbacks}
                categoryName={name}
                rating={feedbacksInfo.averageValue}
                feedbacksCount={feedbacksInfo.count}
              />
            )}
          </div>
        );
      }}
    </CurrentUserConsumer>
  );
};

export default L2Category;
